// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { Info } from 'lucide-react'
import type { OnboardingData } from '@/types'

interface Step3DateOfBirthProps {
  data:     Partial<OnboardingData>
  onChange: (updates: Partial<OnboardingData>) => void
  errors:   Partial<Record<keyof OnboardingData, string>>
}

export function Step3DateOfBirth({ data, onChange, errors }: Step3DateOfBirthProps) {
  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-lg font-semibold text-ink mb-1">Geboortedatum</h2>
        <p className="text-sm text-ink-3">
          Uw geboortedatum is nodig voor de verificatie door uw vertrouwenspersoon.
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="dob" className="text-sm font-medium text-ink-2">
          Geboortedatum <span className="text-sage-600">*</span>
        </label>
        <input
          id="dob"
          type="date"
          value={data.dateOfBirth ?? ''}
          onChange={(e) => onChange({ dateOfBirth: e.target.value })}
          max={new Date().toISOString().split('T')[0]}
          min="1900-01-01"
          className={[
            'w-full rounded-xl border bg-white px-4 py-3',
            'text-sm text-ink',
            'transition-colors duration-150',
            'focus:outline-none focus:ring-2 focus:ring-sage-400 focus:border-transparent',
            errors.dateOfBirth
              ? 'border-status-alertBorder'
              : 'border-sand-300 hover:border-sage-300',
          ].join(' ')}
        />
        {errors.dateOfBirth && (
          <p className="text-xs text-status-alertText" role="alert">
            {errors.dateOfBirth}
          </p>
        )}
      </div>

      {/* Privacy explanation */}
      <div className="flex items-start gap-2.5 rounded-xl bg-status-infoBg border border-sage-200 px-4 py-3.5">
        <Info className="h-4 w-4 text-status-infoText flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-status-infoText">
            Waarom hebben we dit nodig?
          </p>
          <p className="text-xs text-status-infoText/80 mt-0.5 leading-relaxed">
            Uw geboortedatum wordt gebruikt om uw identiteit te verifiëren
            wanneer een vertrouwenspersoon toegang aanvraagt. De datum wordt
            versleuteld opgeslagen en nooit in leesbare vorm gedeeld.
          </p>
        </div>
      </div>
    </div>
  )
}
